import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Container, Stack, Button } from "@mui/material";
import { TextField } from "@mui/material";
import { BsFillTrashFill } from "react-icons/bs";
import {
    fetchAdaRequestsAction,
    fetchAdminPolicyAction,
    fetchAdminCarrierAction,
    discardAdaRequestAction,
} from "../../redux/Actions";
import { strings } from "../../utils/constants";
import NoRecordFound from "../NoRecordFound";
import AdminPagination from "../AdminPagination";
import "../FormManagementTable/FormManagementTable.css";

export const AdaRequestsTable = () => {
    const dispatch = useDispatch();
    const [currentPage, setCurrentPage] = useState(1);
    const [searchText, setSearchText] = useState("");
    const [carrierID, setCarrierID] = useState(strings.general.SELECT_CARRIER);
    const [policyID, setPolicyID] = useState(strings.general.SELECT_BUSINESS_ACTION);

    const { fetchAdminCarrierData } = useSelector((state) => state.adminCarrierReducer);
    const { fetchAdminPolicyData } = useSelector((state) => state.adminPolicyReducer);
    const { adaRequestsData } = useSelector((state) => state.adminAdaRequestsReducer);

    const carrierList = fetchAdminCarrierData?.data ?? [];
    const policyList = fetchAdminPolicyData?.data ?? [];
    const tableRecords = adaRequestsData?.data ?? [];
    const totalPage = Math.ceil(adaRequestsData?.total / 10);

    const getAPI = (page, search, carrier, policy) => {
        dispatch(
            fetchAdaRequestsAction({
                page,
                limit: 10,
                searchText: search,
                carrierId: carrier === strings.general.SELECT_CARRIER ? "" : carrier,
                privacyServiceId: policy === strings.general.SELECT_BUSINESS_ACTION ? "" : policy,
                language: strings.language.EN,
            })
        );
    };

    useEffect(() => {
        getAPI(1, "", strings.general.SELECT_CARRIER, strings.general.SELECT_BUSINESS_ACTION);
        dispatch(fetchAdminCarrierAction({ language: strings.language.EN, isAdmin: true }));
        dispatch(fetchAdminPolicyAction({ language: strings.language.EN, isAdmin: true }));
    }, []);

    const searchHandler = () => {
        setCurrentPage(1);
        getAPI(1, searchText, carrierID, policyID);
    };

    const resetHandler = () => {
        setSearchText("");
        setCurrentPage(1);
        setCarrierID(strings.general.SELECT_CARRIER);
        setPolicyID(strings.general.SELECT_BUSINESS_ACTION);
        getAPI(1, "", strings.general.SELECT_CARRIER, strings.general.SELECT_BUSINESS_ACTION);
    };

    const nextPage = () => {
        if (currentPage !== totalPage) {
            const next = currentPage + 1;
            setCurrentPage(next);
            getAPI(next, searchText, carrierID, policyID);
        }
    };

    const prevPage = () => {
        if (currentPage !== 1) {
            const prev = currentPage - 1;
            setCurrentPage(prev);
            getAPI(prev, searchText, carrierID, policyID);
        }
    };

    const changePage = (id) => {
        setCurrentPage(id);
        getAPI(id, searchText, carrierID, policyID);
    };

    const discardHandler = async (formId) => {
        const result = await dispatch(discardAdaRequestAction({ language: strings.language.EN, formId }));
        if (discardAdaRequestAction.fulfilled.match(result)) {
            getAPI(currentPage, searchText, carrierID, policyID);
        }
    };

    return (
        <>
            <Container className="container-wrapper">
                <Stack
                    direction={{ xs: "column", lg: "row" }}
                    alignItems="end"
                    justifyContent="right"
                    mb={4}
                >
                    <div style={{ minWidth: "226px" }}>
                        <select
                            className="fform-select form-select"
                            id="ada-carrier-list"
                            aria-label="Select carrier"
                            value={carrierID}
                            onChange={(e) => setCarrierID(e.target.value)}
                        >
                            <option value={strings.general.SELECT_CARRIER}>
                                {strings.general.SELECT_CARRIER}
                            </option>
                            {carrierList.map((item) => (
                                <option key={item.id} value={item.id}>
                                    {item.carrier_name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div style={{ minWidth: "226px", marginLeft: "10px" }}>
                        <select
                            className="fform-select form-select"
                            id="ada-policy-list"
                            aria-label="Select business action"
                            value={policyID}
                            onChange={(e) => setPolicyID(e.target.value)}
                        >
                            <option value={strings.general.SELECT_BUSINESS_ACTION}>
                                {strings.general.SELECT_BUSINESS_ACTION}
                            </option>
                            {policyList.map((item) => (
                                <option key={item.id} value={item.id}>
                                    {item.policy_service_name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="finput" style={{ minWidth: "186px" }}>
                        <TextField
                            id="ada-search"
                            variant="outlined"
                            size="small"
                            placeholder={strings.words.SEARCH}
                            sx={{ color: "#184437" }}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            autoComplete="off"
                        />
                    </div>
                    <Button
                        variant="contained"
                        sx={{ color: "#184437" }}
                        className="fbutton"
                        onClick={searchHandler}
                    >
                        {strings.words.SEARCH}
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ color: "#184437" }}
                        className="fresetbutton"
                        onClick={resetHandler}
                    >
                        {strings.words.RESET}
                    </Button>
                </Stack>
            </Container>
            <div className="ftable-wrapper">
                <table className="ftable">
                    <thead>
                        <tr>
                            <th>{strings.general.SR_NO}</th>
                            <th style={{ width: "22%" }}>{strings.general.FORM_NAME_ENGLISH}</th>
                            <th style={{ width: "8%" }}>Language</th>
                            <th>{strings.general.FORM_NO}</th>
                            <th style={{ width: "18%" }}>{strings.general.CARRIER_NAME_ENGLISH}</th>
                            <th style={{ width: "18%" }}>{strings.general.POLICY_NAME_ENGLISH}</th>
                            <th style={{ width: "8%" }}>Request Count</th>
                            <th style={{ width: "6%" }}>{strings.words.ACTIONS}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tableRecords.length > 0 ? (
                            tableRecords.map((row, idx) => (
                                <tr key={row.id}>
                                    <td className="table-cell">
                                        {(currentPage - 1) * 10 + idx + 1}
                                    </td>
                                    <td className="table-cell w-25">{row.form_name}</td>
                                    <td className="table-cell">
                                        {row.form_lang === strings.language.EN
                                            ? strings.words.ENGLISH
                                            : row.form_lang === strings.language.FR
                                                ? strings.words.FRENCH
                                                : ""}
                                    </td>
                                    <td className="table-cell w-25">{row.form_number}</td>
                                    <td className="table-cell w-25">{row.carrier?.carrier_name}</td>
                                    <td className="table-cell w-25">
                                        {row.policy_service?.policy_service_name}
                                    </td>
                                    <td className="table-cell">{row.ada_request_count}</td>
                                    <td className="fit">
                                        <span className="factions mt-1">
                                            <BsFillTrashFill
                                                onClick={() => discardHandler(row.id)}
                                                className="fdelete-btn cursor-pointer"
                                            />
                                        </span>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <NoRecordFound colSpan={8} />
                        )}
                    </tbody>
                </table>
                {tableRecords.length > 0 && (
                    <AdminPagination
                        currentPage={currentPage}
                        totalRecords={adaRequestsData.total}
                        nextPage={nextPage}
                        prevPage={prevPage}
                        changePage={changePage}
                    />
                )}
            </div>
        </>
    );
};
